import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Portal from "@mui/material/Portal";
import Backdrop from "@mui/material/Backdrop";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

import Iconify from "src/components/iconify";
import useResponsive from "src/hooks/useResponsive";
import { useBoolean } from "src/hooks/useBoolean";
import { useCallback, useEffect, useRef, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import Scrollbar from "src/components/scrollbar";
import { useSnackbar } from "src/components/snackbar";
import { useLocales } from "src/locales";
import { formatError } from "src/utils/formatErrorMessage";
import { useAIChatMessage } from "src/hooks/aiChat/useAIChatService";
import { IChatMessage, IQuickAction } from "src/@types/aiChat";
import { CustomAvatar } from "src/components/custom-avatar";
import { useAuthContext } from "src/auth/useAuthContext";

// ----------------------------------------------------------------------

const ZINDEX = 1998;
const POSITION = 24;

interface Message {
  id: string;
  content: string | React.ReactNode;
  sender: "user" | "bot";
  timestamp: Date;
}

interface QuickAction {
  id: string;
  label: string;
  message: string;
  // icon: string;
}

type Props = {
  onCloseCompose: VoidFunction;
};

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: "1",
    label: "Today's energy",
    message: "How much energy did my fleet consume today?",
    // icon: "solar:battery-charge-bold",
  },
  {
    id: "2",
    label: "This week's kilometers",
    message: "How many kilometers did my vehicles travel this week?",
    // icon: "solar:road-bold",
  },
  {
    id: "3",
    label: "Moving vehicles",
    message: "Which vehicles are currently moving?",
    // icon: "solar:car-linear",
  },
];

export default function ChatContainer({ onCloseCompose }: Props) {
  const theme = useTheme();
  const smUp = useResponsive("up", "sm");
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const { user } = useAuthContext();

  const fullScreen = useBoolean();
  const minimized = useBoolean();
  const [messages, setMessages] = useState<IChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");

  const { sendChatMessage, loading: isLoading } = useAIChatMessage();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Disable/enable background scrolling based on fullscreen mode
  useEffect(() => {
    if ((fullScreen.value || !smUp) && !minimized.value) {
      // Disable background scrolling in fullscreen mode (but not when minimized)
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable background scrolling in normal mode or when minimized
      document.body.style.overflow = "auto";
    }

    // Cleanup on unmount or when chat is closed
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [fullScreen.value, smUp, minimized.value]);

  // Add welcome message on mount
  useEffect(() => {
    const welcomeMessage: Message = {
      id: "welcome",
      content: `Welcome! I'm your e-mobility fleet management assistant. I can help you with:\n\n• 🔋 Energy consumption analysis\n• 🔌 Charging session insights\n• 🚗 Vehicle kilometers and trip data\n• 📍 Real-time vehicle status\n\nHow can I assist you today?`,
      sender: "bot",
      timestamp: new Date(),
    };
    setMessages([welcomeMessage]);
  }, []);

  // Auto scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, []);

  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);

    return () => clearTimeout(timer);
  }, [messages, scrollToBottom]);

  // Scroll to bottom when returning from minimized mode
  useEffect(() => {
    if (!minimized.value && messages.length > 0) {
      // Add a longer delay to ensure the chat container is fully rendered
      const timer = setTimeout(() => {
        scrollToBottom();
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [minimized.value, scrollToBottom, messages.length]);

  // Fleet data rendering function
  const renderFleetConsumption = useCallback(
    (response: any): React.ReactNode => {
      const functions = response.data.functions_called ?? [];
      const requestedMetrics = response.data.raw_data?.requested_metrics ?? [];
      const consumptionData =
        response.data.raw_data?.data?.energy_consumption?.data ?? [];

      if (
        !functions.includes("get_fleet_data") ||
        !requestedMetrics.includes("energy_consumption") ||
        consumptionData.length === 0
      ) {
        return null;
      }

      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "primary.main" }}>
            Energy consumption
          </Typography>
          <Stack spacing={1}>
            {consumptionData.map((item: any) => (
              <Paper
                key={item.vehicle_id}
                sx={{
                  p: 1.5,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack>
                    <Typography variant="body2" fontWeight="bold">
                      {item.vehicle_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.date}
                    </Typography>
                  </Stack>
                  <Chip
                    label={`${item.consumption} kWh`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      );
    },
    []
  );

  // Fleet kilometers traveled rendering function
  const renderFleetKilometers = useCallback(
    (response: any): React.ReactNode => {
      const functions = response.data.functions_called ?? [];
      const requestedMetrics = response.data.raw_data?.requested_metrics ?? [];
      const kilometersData =
        response.data.raw_data?.data?.kilometers_traveled?.data ?? [];

      if (
        !functions.includes("get_fleet_data") ||
        !requestedMetrics.includes("kilometers_traveled") ||
        kilometersData.length === 0
      ) {
        return null;
      }

      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "primary.main" }}>
            Total distance traveled
          </Typography>
          <Stack spacing={1}>
            {kilometersData.map((item: any) => (
              <Paper
                key={item.vehicle_id}
                sx={{
                  p: 1.5,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack>
                    <Typography variant="body2" fontWeight="bold">
                      {item.vehicle_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.date}
                    </Typography>
                  </Stack>
                  <Chip
                    label={`${item.kilometers} km`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      );
    },
    []
  );

  // Currently moving vehicles rendering function
  const renderCurrentlyMoving = useCallback(
    (response: any): React.ReactNode => {
      const functions = response.data.functions_called ?? [];
      const requestedMetrics = response.data.raw_data?.requested_metrics ?? [];
      const movingData =
        response.data.raw_data?.data?.currently_moving?.data ?? [];

      if (
        !functions.includes("get_fleet_data") ||
        !requestedMetrics.includes("currently_moving") ||
        movingData.length === 0
      ) {
        return null;
      }

      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "primary.main" }}>
            Currently moving vehicles
          </Typography>
          <Stack spacing={1}>
            {movingData.map((item: any) => (
              <Paper
                key={item.vehicle_id}
                sx={{
                  p: 1.5,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" fontWeight="bold">
                      {item.vehicle_name}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={`${item.speed} km/h`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                      <Chip
                        label={`${item.battery_level}%`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    📍 {item.location?.address || "Location not available"}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      );
    },
    []
  );

  // Charging sessions rendering function
  const renderChargingSessions = useCallback(
    (response: any): React.ReactNode => {
      const functions = response.data.functions_called ?? [];
      const requestedMetrics = response.data.raw_data?.requested_metrics ?? [];
      const chargingData =
        response.data.raw_data?.data?.charging_sessions?.data ?? [];

      if (
        !functions.includes("get_fleet_data") ||
        !requestedMetrics.includes("charging_sessions") ||
        chargingData.length === 0
      ) {
        return null;
      }

      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "primary.main" }}>
            Charging sessions
          </Typography>
          <Stack spacing={1}>
            {chargingData.map((item: any) => (
              <Paper
                key={item.vehicle_id}
                sx={{
                  p: 1.5,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack>
                      <Typography variant="body2" fontWeight="bold">
                        {item.vehicle_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.date}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                      <Chip
                        label={`${item.sessions} sessions`}
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                      <Chip
                        label={`${item.total_time} min`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      );
    },
    []
  );

  // Trip data rendering function
  const renderTripData = useCallback((response: any): React.ReactNode => {
    const functions = response.data.functions_called ?? [];
    const requestedMetrics = response.data.raw_data?.requested_metrics ?? [];
    const tripData = response.data.raw_data?.data?.trip_data?.data ?? [];
    const summary = response.data.raw_data?.data?.trip_data?.summary;

    if (
      !functions.includes("get_fleet_data") ||
      !requestedMetrics.includes("trip_data") ||
      tripData.length === 0
    ) {
      return null;
    }

    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
        <Typography variant="body2" sx={{ mb: 2, color: "primary.main" }}>
          Trip Data
        </Typography>
        <Stack spacing={1}>
          {tripData.map((item: any) => (
            <Paper
              key={item.vehicle_id}
              sx={{
                p: 1.5,
                bgcolor: "white",
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack>
                    <Typography variant="body2" fontWeight="bold">
                      {item.vehicle_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.date}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={`${item.trips} trips`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={`${item.avg_trip_distance} km avg`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          ))}
          {summary && (
            <Paper
              sx={{
                p: 1.5,
                bgcolor: "primary.50",
                border: "2px solid",
                borderColor: "primary.200",
              }}
            >
              <Stack spacing={0.5}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color="primary.main"
                >
                  Summary ({summary.vehicle_count} vehicles)
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    Total trips: {summary.total_trips}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg per vehicle: {summary.average_trips_per_vehicle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Overall avg distance: {summary.overall_avg_trip_distance} km
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>
    );
  }, []);

  // Handle send message
  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: IChatMessage = {
      id: Date.now().toString(),
      content: inputMessage.trim(),
      sender: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentMessage = inputMessage.trim();
    setInputMessage("");

    try {
      const response = await sendChatMessage({
        message: currentMessage,
      });

      if (response.success && response.data.text_response) {
        // Check if we have fleet data to render
        const fleetConsumptionNode = renderFleetConsumption(response);
        const fleetKilometersNode = renderFleetKilometers(response);
        const currentlyMovingNode = renderCurrentlyMoving(response);
        const chargingSessionsNode = renderChargingSessions(response);
        const tripDataNode = renderTripData(response);

        const botResponse: IChatMessage = {
          id: (Date.now() + 1).toString(),
          content:
            fleetConsumptionNode ||
            fleetKilometersNode ||
            currentlyMovingNode ||
            chargingSessionsNode ||
            tripDataNode ? (
              <>
                {response.data.text_response}
                {fleetConsumptionNode}
                {fleetKilometersNode}
                {currentlyMovingNode}
                {chargingSessionsNode}
                {tripDataNode}
              </>
            ) : (
              response.data.text_response
            ),
          sender: "bot",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botResponse]);

        // Log raw data for debugging if available
        if (response.data.raw_data) {
          console.log("Raw fleet data:", response.data.raw_data);
        }
      } else {
        throw new Error(response.message || "No response received");
      }
    } catch (error) {
      console.error("Chat error:", error);

      const errorMessage = formatError(error, translate);

      const botErrorResponse: IChatMessage = {
        id: (Date.now() + 1).toString(),
        content: `Sorry, I encountered an error: ${errorMessage}. Please try again later.`,
        sender: "bot",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botErrorResponse]);

      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  }, [
    inputMessage,
    isLoading,
    sendChatMessage,
    translate,
    enqueueSnackbar,
    renderFleetConsumption,
    renderFleetKilometers,
    renderCurrentlyMoving,
    renderChargingSessions,
    renderTripData,
  ]);

  // Handle quick action click
  const handleQuickAction = useCallback(
    (action: IQuickAction) => {
      setInputMessage(action.message);
      // Auto-send after setting message
      setTimeout(() => {
        if (!isLoading) {
          handleSendMessage();
        }
      }, 100);
    },
    [handleSendMessage, isLoading]
  );

  // Handle enter key
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  // Render message
  const renderMessage = useCallback(
    (message: Message) => {
      const isUser = message.sender === "user";
      const isBot = message.sender === "bot";

      return (
        <Stack
          key={message.id}
          direction={isUser ? "row-reverse" : "row"}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          {isBot ? (
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "primary.main",
                color: "white",
                fontSize: "1.2rem",
              }}
            >
              <Iconify icon="solar:chat-round-dots-bold" />
            </Avatar>
          ) : (
            <CustomAvatar
              src={user?.profilePictureURL}
              alt={user?.displayName}
              firstName={user?.firstName}
              lastName={user?.lastName}
              sx={{
                width: 40,
                height: 40,
                fontSize: "1rem",
              }}
            />
          )}

          <Paper
            sx={{
              p: 1.5,
              maxWidth: "70%",
              bgcolor: isUser ? "primary.main" : "background.paper",
              color: isUser ? "primary.contrastText" : "text.primary",
              borderRadius: 2,
              ...(isUser && {
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              }),
            }}
          >
            {typeof message.content === "string" ? (
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                }}
              >
                {message.content}
              </Typography>
            ) : (
              <Box sx={{ lineHeight: 1.5 }}>{message.content}</Box>
            )}
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                opacity: 0.7,
                fontSize: "0.75rem",
              }}
            >
              {message.timestamp.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          </Paper>
        </Stack>
      );
    },
    [theme.palette.primary, user]
  );

  // Determine container height and width based on state
  const getContainerStyles = useCallback(() => {
    if (minimized.value) {
      return {
        width: 320,
        height: "auto",
        maxHeight: "none",
      };
    }

    if (fullScreen.value) {
      return {
        width: `calc(100% - ${POSITION}px)`,
        height: `calc(100% - ${POSITION}px)`,
        maxHeight: "none",
        m: 0,
        right: POSITION / 2,
        bottom: POSITION / 2,
      };
    }

    return {
      width: 420,
      height: 600,
      maxHeight: "calc(100vh - 100px)",
    };
  }, [minimized.value, fullScreen.value]);

  function getRandomTypingMessage(): string {
    const messages = [
      "Thinking…",
      "Working on it…",
      "Hang on, I’m checking that…",
      "Just a moment…",
      "Getting your answer…",
      "Let me check that…",
      "One sec, working on it…",
      "Almost there…",
    ];

    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  }

  return (
    <Portal>
      {(fullScreen.value || !smUp) && !minimized.value && (
        <Backdrop open sx={{ zIndex: ZINDEX }} />
      )}

      <Paper
        sx={{
          right: 0,
          bottom: 0,
          borderRadius: 2,
          display: "flex",
          position: "fixed",
          zIndex: ZINDEX + 1,
          m: `${POSITION}px`,
          overflow: "hidden",
          flexDirection: "column",
          boxShadow: (theme) => theme.customShadows.dropdown,
          ...getContainerStyles(),
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            p: 2,
            flexShrink: 0,
            cursor: minimized.value ? "pointer" : "default",
          }}
          onClick={minimized.value ? minimized.onFalse : undefined}
        >
          <Avatar
            sx={{
              width: minimized.value ? 32 : 48,
              height: minimized.value ? 32 : 48,
              bgcolor: alpha(theme.palette.common.white, 0.2),
              color: "white",
              mr: 1.5,
              transition: "all 0.3s ease",
            }}
          >
            <Iconify icon="solar:chat-round-dots-bold" />
          </Avatar>

          <Stack sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: minimized.value ? "0.95rem" : "1.1rem",
                fontWeight: 600,
                transition: "all 0.3s ease",
              }}
            >
              Keon AI Assistant
            </Typography>
            {!minimized.value && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Online
                </Typography>
              </Stack>
            )}
          </Stack>

          {!minimized.value && (
            <IconButton
              onClick={minimized.onTrue}
              sx={{ color: "white", mr: 0.5 }}
              title="Minimize"
            >
              <Iconify icon="eva:minus-fill" />
            </IconButton>
          )}

          <IconButton
            onClick={fullScreen.onToggle}
            sx={{ color: "white", mr: 0.5 }}
            title={fullScreen.value ? "Exit fullscreen" : "Fullscreen"}
          >
            <Iconify
              icon={fullScreen.value ? "eva:collapse-fill" : "eva:expand-fill"}
            />
          </IconButton>

          <IconButton
            onClick={onCloseCompose}
            sx={{ color: "white" }}
            title="Close"
          >
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>

        {/* Show minimized state indicator */}
        {minimized.value && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              py: 1,
              px: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              cursor: "pointer",
            }}
            onClick={minimized.onFalse}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Iconify icon="eva:arrow-up-fill" width={14} />
              Click to expand chat
            </Typography>
            {messages.length > 1 && (
              <Chip
                size="small"
                label={`${messages.length} messages`}
                sx={{
                  ml: 1,
                  height: 20,
                  fontSize: "0.7rem",
                  bgcolor: "primary.main",
                  color: "white",
                }}
              />
            )}
          </Stack>
        )}

        {/* Messages - Hidden when minimized */}
        {!minimized.value && (
          <Box
            className="makis"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              backgroundColor: "#fafafa",
            }}
          >
            <Scrollbar
              sx={{
                flex: 1,
                p: 2,
                bgcolor: "grey.50",
                minHeight: 0,
                "& .simplebar-content": {
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "100%",
                },
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent:
                    messages.length <= 1 ? "flex-start" : "flex-end",
                  minHeight: "100%",
                }}
              >
                {messages.map(renderMessage)}

                {isLoading && (
                  <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "primary.main",
                        color: "white",
                      }}
                    >
                      <Iconify icon="solar:chat-round-dots-bold" />
                    </Avatar>

                    <Paper
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        {getRandomTypingMessage()}
                      </Typography>
                    </Paper>
                  </Stack>
                )}

                <div ref={messagesEndRef} />
              </Box>
            </Scrollbar>
          </Box>
        )}

        {/* Quick Actions - Hidden when minimized */}
        {!minimized.value && (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              p: 1.5,
              overflowX: "auto",
              flexShrink: 0,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {QUICK_ACTIONS.map((action) => (
              <Chip
                key={action.id}
                label={action.label}
                // icon={<Iconify icon={action.icon} width={16} />}
                onClick={() => handleQuickAction(action)}
                disabled={isLoading}
                sx={{
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "white",
                    "& .MuiChip-icon": {
                      color: "white",
                    },
                  },
                  "&:disabled": {
                    opacity: 0.6,
                    cursor: "not-allowed",
                  },
                }}
              />
            ))}
          </Stack>
        )}

        {!minimized.value && <Divider />}

        {/* Input - Hidden when minimized */}
        {!minimized.value && (
          <Stack sx={{ p: 2, flexShrink: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-end"
              sx={{
                bgcolor: "grey.100",
                borderRadius: 3,
                p: 1,
                border: "2px solid",
                borderColor: "grey.300",
                "&:focus-within": {
                  borderColor: "primary.main",
                  bgcolor: "white",
                },
              }}
            >
              <TextField
                ref={inputRef}
                multiline
                maxRows={3}
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                sx={{
                  flexGrow: 1,
                  "& .MuiOutlinedInput-root": {
                    border: "none",
                    "& fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiInputBase-input": {
                    p: 0,
                    maxHeight: "72px",
                    overflowY: "auto",
                  },
                }}
              />

              <IconButton
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  "&:disabled": {
                    bgcolor: "grey.300",
                    color: "grey.500",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Iconify icon="iconamoon:send-fill" />
                )}
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Portal>
  );
}
